import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnDestroy,
    OnInit
} from '@angular/core';
import { LgvPricingModel } from '../../models/pricing.model';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import {
    LgvClientService,
    NotificationService,
    LgvUserService,
    LgvTSHelper
} from '../../../../core/core.module';
import { LgvPricingService } from '../../services/pricing.service';
import { LockRequestModalComponent } from '../../../../shared/lock-request-modal/lock-request-modal.component';
import { BehaviorSubject, first, Subject, take, takeUntil } from 'rxjs';
import { LgvModel } from '../../../../data-models/longevity.model';
import { LoanAppPageService } from '../../../../core/services/page-services/loan-app-page.service';
import { ConfirmationDialogComponent } from '../../../../shared/confirmation-dialog/confirmation-dialog.component';

@Component({
    selector: 'lgv-scenario-info-card',
    templateUrl: './scenario-info-card.component.html',
    styleUrls: ['./scenario-info-card.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ScenarioInfoCardComponent implements OnInit, OnDestroy {
    @Input() pricingScenario: LgvPricingModel.PricingScenario;
    @Input() sourcePage: string;
    @Input() expanded = false;
    opportunities$ = new BehaviorSubject<LgvModel.Opportunity[]>(null);
    isLoading$ = new BehaviorSubject(false);
    onDestroy$ = new Subject();
    isCurrentPreApproved$ = new BehaviorSubject(false);

    constructor(
        public lgvClientSvc: LgvClientService,
        private router: Router,
        private notificationSvc: NotificationService,
        private lgvPricingSvc: LgvPricingService,
        public lgvUserSvc: LgvUserService,
        private matDialog: MatDialog,
        private lgvLoanAppPageSvc: LoanAppPageService
    ) {}

    ngOnInit(): void {
        this.lgvClientSvc.IsOpportunitiesReady$.pipe(first(x => !!x)).subscribe(
            () => {
                this.opportunities$.next(
                    this.getOpportunitiesForThisScenario()
                );
            }
        );
        this.pricingScenario = {
            ...this.pricingScenario,
            isSelected: this.expanded
        };
        const currentPA = this.lgvPricingSvc.checkIfCurrentPreApprovedPricing(
            this.pricingScenario
        );
        this.isCurrentPreApproved$.next(currentPA);
    }

    ngOnDestroy(): void {
        this.onDestroy$.next(null);
        this.onDestroy$.complete();
    }

    get hasValidRequest() {
        return this.pricingScenario?.request
            ? this.pricingScenario?.request
            : this.pricingScenario?.dealDeskRequest;
    }

    getOpportunitiesForThisScenario(): LgvModel.Opportunity[] {
        const allOpportunities = this.lgvClientSvc.allOpportunities$?.value;
        if (!allOpportunities) {
            return [];
        }
        return allOpportunities?.filter(
            f =>
                f.opportunityInfo.pricingScenarioId === this.pricingScenario?.id
        );
    }

    onScenarioClick(scenarioID: string) {
        if (this.sourcePage == 'ClientPage') {
            this.router.navigate(
                [
                    `/admin/pricing-scenario/agent/${this.lgvUserSvc.AgentSS$.value.agentInfo.id}/client/${this.lgvClientSvc.ClientID$.value}/scenario/${scenarioID}`
                ],
                {
                    queryParams: {
                        from: 'client-overview'
                    }
                }
            );
        } else if (this.sourcePage == 'LoanPipeline') {
            this.router.navigate(
                [
                    `/admin/pricing-scenario/client/${this.lgvClientSvc.ClientID$.value}/scenario/${scenarioID}/loanApp/${this.pricingScenario?.loanApplicationId}`
                ],
                {
                    queryParams: {
                        from: 'loan-app-quotes'
                    }
                }
            );
        }
    }

    cancelLockRequest(ps: LgvPricingModel.PricingScenario) {
        const dialogRef = this.matDialog.open(ConfirmationDialogComponent, {
            width: '450px',
            data: {
                header: 'Cancel Lock Request',
                Confirmation: `Cancelling this lock request will move the status back to Ready to Lock. All parties will receive an update on this lock request status change.`
            }
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(x => {
                if (x.action === true) {
                    this.lgvPricingSvc
                        .cancelLockRequest(ps?.id)
                        .pipe(take(1))
                        .subscribe(x => {
                            this.notificationSvc.success(
                                'Lock Request has been cancelled.'
                            );
                        });
                }
            });
    }

    onRefreshOpportunity(ps: LgvPricingModel.PricingScenario) {
        this.lgvClientSvc.LoadOpportunitiesForPricingScenario(ps, true);
    }

    onSendLockRequest(ps: LgvPricingModel.PricingScenario) {
        if (this.lgvUserSvc.isPreLicensedAgent) {
            this.lgvUserSvc.showPreLicensingModal();
            return;
        }
        this.lgvClientSvc.OpportunitiesState$.next('Loading');

        const dialogRef = this.matDialog.open(LockRequestModalComponent, {
            width: '750px',
            disableClose: true,
            data: {
                agentSS: this.lgvUserSvc.AgentSS$.value,
                pricingScenario: ps,
                loanApplication: this.lgvLoanAppPageSvc.selectedLoanApplication$
                    .value
            }
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(x => {
                this.lgvClientSvc.OpportunitiesState$.next('Ready');
            });
    }

    onDeleteScenario(ps: LgvPricingModel.PricingScenario) {
        if (this.lgvUserSvc.isPreLicensedAgent) {
            this.lgvUserSvc.showPreLicensingModal();
            return;
        }
        this.isLoading$.next(true);
        this.lgvPricingSvc
            .deleteScenario(ps.id)
            .pipe(take(1))
            .subscribe(
                x => {
                    this.lgvClientSvc.loadScenarios(
                        this.lgvClientSvc.ClientID$.value
                    );
                    this.isLoading$.next(false);
                    this.notificationSvc.success('Scenario has been deleted');
                },
                err => {
                    this.isLoading$.next(false);
                    console.log('Error deleting scenario:');
                    console.log(err);
                    this.notificationSvc.error(
                        'There was an error trying to delete the scenario'
                    );
                }
            );
    }

    onDuplicateScenario(origPS: LgvPricingModel.PricingScenario) {
        if (this.lgvUserSvc.isPreLicensedAgent) {
            this.lgvUserSvc.showPreLicensingModal();
            return;
        }
        let currentTime = new Date();
        let curJSTime = LgvTSHelper.jsDateToJsonDate(currentTime);

        let clonePS = { ...origPS };
        clonePS.name = this.findDuplicateName(clonePS.name);
        clonePS.id = null;
        clonePS.createdTimestamp = curJSTime;
        clonePS.updatedTimestamp = curJSTime;

        this.isLoading$.next(true);
        this.lgvPricingSvc
            .addPricingScenario(clonePS)
            .pipe(take(1))
            .subscribe(
                x => {
                    this.isLoading$.next(false);
                    this.notificationSvc.success('Scenario has been copied');

                    this.lgvClientSvc.loadScenarios(
                        this.lgvClientSvc.ClientID$.value
                    );
                },
                err => {
                    this.isLoading$.next(false);
                    this.notificationSvc.error(
                        'There was an error copying the scenario: ' + err
                    );
                }
            );
    }

    findDuplicateName(origName: string): string {
        let highNum = 0;
        let prefix = origName;

        let copyIndex = origName.lastIndexOf(' (copy ');
        if (copyIndex !== -1) {
            prefix = origName.substr(0, copyIndex);
        }

        let existingScenarios = this.lgvClientSvc.pricingScenarios$.value;
        if (existingScenarios && existingScenarios.length > 0) {
            for (let ps of existingScenarios) {
                let name = ps.name as string;

                if (
                    name &&
                    name.includes('(copy') &&
                    name.startsWith(prefix) &&
                    name.endsWith(')')
                ) {
                    if (name.endsWith('(copy)')) {
                        // treat this like (copy 1)
                        highNum = Math.max(highNum, 1);
                    } else {
                        let tokens = name.split(' ');
                        if (tokens && tokens.length > 0) {
                            let lastToken = tokens[tokens.length - 1];

                            // strip the trailing ")"
                            lastToken = lastToken.substr(
                                0,
                                lastToken.length - 1
                            );

                            let numTest = +lastToken;
                            let isNumeric = !isNaN(numTest);
                            if (isNumeric) {
                                highNum = Math.max(highNum, numTest);
                            }
                        }
                    }
                }
            }
        }

        if (highNum > 0) {
            return prefix + ` (copy ${++highNum})`;
        } else {
            return origName + ' (copy)';
        }
    }
}
